// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback } from 'react';

import { t } from '@/i18n';
import type { BffWebSocketClient } from '@/services/bffWebSocket';

type UseHoleActionsParams = {
  debug: boolean;
  holeNumber: number;
  roundId: string | null;
  canMutateRoundStatus: boolean;
  router: {
    push: (path: string) => void;
  };
  selectedGameDefinitionIds: string[];
  setHole: (hole: number) => void;
  socketClient: BffWebSocketClient | null;
  logWithContext: (event: string, payload: Record<string, unknown>, level?: 'debug' | 'warn' | 'error') => void;
};

export const useHoleActions = ({
  debug,
  holeNumber,
  roundId,
  canMutateRoundStatus,
  router,
  selectedGameDefinitionIds,
  setHole,
  socketClient,
  logWithContext,
}: UseHoleActionsParams) => {
  const navigateToHole = useCallback(
    (targetHole: number) => {
      if (!roundId) return;
      setHole(targetHole);
      const params: string[] = [];
      if (debug) params.push('debug=true');
      if (selectedGameDefinitionIds.length > 0) {
        params.push(`gameDefinitionIds=${encodeURIComponent(selectedGameDefinitionIds.join(','))}`);
      }
      const dir = targetHole > holeNumber ? 1 : -1;
      params.push(`dir=${dir}`);
      const query = params.length ? `?${params.join('&')}` : '';
      router.push(`/round/${roundId}/hole/${targetHole}/compact${query}`);
    },
    [debug, holeNumber, roundId, router, selectedGameDefinitionIds, setHole]
  );

  const finishRound = useCallback(() => {
    if (!socketClient || !roundId) return;

    if (canMutateRoundStatus) {
      const sent = socketClient.send({ type: 'round.status.update', roundId, status: 'finished' });
      if (!sent) {
        logWithContext(
          'hole.round.finishSendFailed',
          { message: t('hole.finishSendFailed'), roundId },
          'warn'
        );
      }
    }

    const params: string[] = ['from=hole'];
    if (debug) params.push('debug=true');
    const query = params.length ? `?${params.join('&')}` : '';
    router.push(`/round/${roundId}/summary${query}`);
  }, [canMutateRoundStatus, debug, logWithContext, roundId, router, socketClient]);

  return {
    finishRound,
    navigateToHole,
  };
};
